import axios from 'axios'
import jwtDefaultConfig from './jwtDefaultConfig'

export default class JwtService {
  jwtConfig = { ...jwtDefaultConfig }
  isAlreadyFetchingAccessToken = false
  subscribers = []

  constructor(jwtOverrideConfig) {
    this.jwtConfig = { ...this.jwtConfig, ...jwtOverrideConfig }

    // Request interceptor - This is where we handle the queueing
    axios.interceptors.request.use(
      async config => {
        // Skip token refresh for login, register, and refresh endpoints
        const isAuthEndpoint = [
          this.jwtConfig.loginEndpoint,
          this.jwtConfig.registerEndpoint,
          this.jwtConfig.refreshEndpoint
        ].some(endpoint => config.url?.includes(endpoint))

        if (isAuthEndpoint) {
          return config
        }

        const accessToken = this.getToken()
        
        if (!accessToken) {
          return config
        }

        // If token refresh is in progress, queue this request
        if (this.isAlreadyFetchingAccessToken) {
          console.log('Queueing request during token refresh:', config.url)
          return new Promise((resolve, reject) => {
            this.addSubscriber((newToken, error) => {
              if (error) {
                console.log('Request failed due to refresh error:', config.url)
                reject(error)
              } else {
                console.log('Retrying queued request with new token:', config.url)
                config.headers.Authorization = `${this.jwtConfig.tokenType} ${newToken}`
                resolve(config)
              }
            })
          })
        }

        // Check if token is expired and refresh if needed
        if (this.isTokenExpired(accessToken)) {
          console.log('Token expired, refreshing before request:', config.url)
          this.isAlreadyFetchingAccessToken = true
          
          try {
            const res = await this.refreshToken()
            const newAccessToken = res.data.data.accessToken
            const newRefreshToken = res.data.data.refreshToken
            
            this.setToken(newAccessToken)
            this.setRefreshToken(newRefreshToken)
            
            // Notify all queued requests
            this.onAccessTokenFetched(newAccessToken)
            
            config.headers.Authorization = `${this.jwtConfig.tokenType} ${newAccessToken}`
            console.log('Token refreshed successfully for request:', config.url)
            
          } catch (error) {
            console.error('Token refresh failed in request interceptor:', error)
            this.onRefreshTokenFailed(error)
            this.clearUserData()
            window.location = '/login'
            throw error
          } finally {
            this.isAlreadyFetchingAccessToken = false
          }
        } else {
          // Token is valid, attach it
          config.headers.Authorization = `${this.jwtConfig.tokenType} ${accessToken}`
        }

        return config
      },
      error => Promise.reject(error)
    )

    // Response interceptor - Fallback for any 401s that slip through
    axios.interceptors.response.use(
      response => response,
      async error => {
        const { config, response } = error
        const originalRequest = config

        // Only handle 401s that aren't from auth endpoints and haven't been retried
        if (response?.status === 401 && !originalRequest._retry) {
          const isAuthEndpoint = [
            this.jwtConfig.loginEndpoint,
            this.jwtConfig.registerEndpoint,
            this.jwtConfig.refreshEndpoint
          ].some(endpoint => originalRequest.url?.includes(endpoint))

          if (isAuthEndpoint) {
            return Promise.reject(error)
          }

          console.log('Got 401 in response interceptor for:', originalRequest.url)
          
          if (this.isAlreadyFetchingAccessToken) {
            console.log('Queueing 401 retry during token refresh:', originalRequest.url)
            return new Promise((resolve, reject) => {
              this.addSubscriber((accessToken, refreshError) => {
                if (refreshError) {
                  reject(refreshError)
                } else {
                  originalRequest.headers.Authorization = `${this.jwtConfig.tokenType} ${accessToken}`
                  originalRequest._retry = true
                  resolve(axios(originalRequest))
                }
              })
            })
          }

          originalRequest._retry = true
          this.isAlreadyFetchingAccessToken = true

          try {
            const res = await this.refreshToken()
            const newAccessToken = res.data.data.accessToken
            const newRefreshToken = res.data.data.refreshToken
            
            this.setToken(newAccessToken)
            this.setRefreshToken(newRefreshToken)
            
            originalRequest.headers.Authorization = `${this.jwtConfig.tokenType} ${newAccessToken}`
            this.onAccessTokenFetched(newAccessToken)
            
            console.log('Token refreshed in response interceptor, retrying:', originalRequest.url)
            return axios(originalRequest)
            
          } catch (refreshError) {
            console.error('Token refresh failed in response interceptor:', refreshError)
            this.onRefreshTokenFailed(refreshError)
            this.clearUserData()
            window.location = '/login'
            return Promise.reject(refreshError)
          } finally {
            this.isAlreadyFetchingAccessToken = false
          }
        }

        return Promise.reject(error)
      }
    )
  }

  // Notify all queued requests with the new token
  onAccessTokenFetched(accessToken) {
    console.log(`Notifying ${this.subscribers.length} queued requests with new token`)
    this.subscribers.forEach(callback => callback(accessToken, null))
    this.subscribers = []
  }

  // Notify all queued requests about refresh failure
  onRefreshTokenFailed(error) {
    console.log(`Notifying ${this.subscribers.length} queued requests about refresh failure`)
    this.subscribers.forEach(callback => callback(null, error))
    this.subscribers = []
  }

  // Add a request to the queue
  addSubscriber(callback) {
    this.subscribers.push(callback)
  }

  // Check if token is expired (with 30 second buffer)
  isTokenExpired(token) {
    if (!token) return true
    
    try {
      const payload = JSON.parse(atob(token.split('.')[1]))
      const currentTime = Math.floor(Date.now() / 1000)
      // Add 30 second buffer to refresh before actual expiration
      return payload.exp < (currentTime + 30)
    } catch (error) {
      console.error('Error parsing token:', error)
      return true // Treat invalid tokens as expired
    }
  }

  // Get the access token from localStorage
  getToken() {
    return localStorage.getItem(this.jwtConfig.storageTokenKeyName)
  }

  // Get the refresh token from localStorage
  getRefreshToken() {
    return localStorage.getItem(this.jwtConfig.storageRefreshTokenKeyName)
  }

  // Set the access token in localStorage
  setToken(value) {
    localStorage.setItem(this.jwtConfig.storageTokenKeyName, value)
  }

  // Set the refresh token in localStorage
  setRefreshToken(value) {
    localStorage.setItem(this.jwtConfig.storageRefreshTokenKeyName, value)
  }

  // Clear user data from localStorage
  clearUserData() {
    localStorage.removeItem('userData')
    localStorage.removeItem(this.jwtConfig.storageTokenKeyName)
    localStorage.removeItem(this.jwtConfig.storageRefreshTokenKeyName)
  }

  // Login method
  async login(...args) {
    const response = await axios.post(this.jwtConfig.loginEndpoint, ...args)
    const { accessToken, refreshToken } = response.data.data
    this.setToken(accessToken)
    this.setRefreshToken(refreshToken)
    return response
  }

  // Register method
  async register(...args) {
    const response = await axios.post(this.jwtConfig.registerEndpoint, ...args)
    return response
  }

  // Refresh token method
  async refreshToken() {
    const token = this.getToken()
    let refreshToken = this.getRefreshToken()
    
    // Validate tokens exist
    if (!token || !refreshToken) {
      throw new Error('No tokens available for refresh')
    }
    
    // Clean refresh token - remove quotes if they exist
    if (refreshToken.startsWith('"') && refreshToken.endsWith('"')) {
      refreshToken = refreshToken.slice(1, -1)
    }

    console.log('Making refresh token request')
    const response = await axios.put(this.jwtConfig.refreshEndpoint, { 
      token, 
      refreshToken 
    })
    return response
  }
}